<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">{{ modalTitle }}</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.locationDetails" class="form-wrapper">
                <div class="form-container location-form-container">
                    <form-group name="locationName" label="Location Name">
                        <md-input v-focus v-model="locationDetails.locationName" />
                    </form-group>
                    <form-group name="barcode" label="Barcode (optional - auto-generated if left blank)">
                        <md-input v-model="locationDetails.barcode" />
                    </form-group>
                    <form-group name="notes" label="Notes">
                        <md-input v-model="locationDetails.notes" />
                    </form-group>
                    <div class="color-form-group">
                        <form-group name="color" label="Color">
                            <md-input v-model="locationDetails.color" />
                        </form-group>
                        <input type="color" v-model="locationDetails.color" class="color-picker" />
                    </div>

                    <form-group name="locationType" label="Type">
                        <md-select v-model="locationDetails.locationType">
                            <md-option v-for="choice in locationChoices" :key="choice" :value="choice">
                                {{ choice }}
                            </md-option>
                        </md-select>
                    </form-group>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" @click.stop="save">
                {{ isUpdateFlag ? 'Update' : 'Create' }}
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
        </div>
    </div>
</template>
<script>
import { GeneralMixin } from '@/mixins';
import { maxLength, required } from 'vuelidate/lib/validators';
import { handleRequests, showErrorMessage } from '@/helpers';

export default {
    name: 'ManageLocationModal',
    mixins: [GeneralMixin],
    props: {
        // warehouse id is required for creating and updating a location
        warehouseId: {
            type: Number,
            default: null
        },
        assetId: {
            type: Number,
            default: null
        },
        teamMemberPublicId: {
            type: String,
            default: null
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        // The locationId and initialLocationDetails should be null if the modal is for creating a new location
        locationId: {
            type: Number,
            default: null
        },
        initialLocationDetails: {
            type: Object,
            default: null
        },
        isUpdateFlag: {
            type: Boolean,
            default: false
        },
        locationChoices: {
            type: Array,
            default: () => []
        }
    },
    mounted() {
        if (this.isUpdateFlag) {
            const { name, type, ...rest } = this.initialLocationDetails;
            this.locationDetails = { ...rest, locationName: name, locationType: type };
            return;
        }
        this.locationDetails.color = this.generateRandomHexColor();
    },
    data() {
        return {
            locationDetails: {
                // even though the model property is "name", I had to rename it to "locationName"
                // because of the global vuelidate error message which uses the property name
                // open the root main.js file for more details
                barcode: '',
                locationName: '',
                notes: '',
                color: '',

                // even though the model property is "type", I had to rename it to "locationType"
                // because of the global vuelidate error message which uses the property type
                // open the root main.js file for more details
                locationType: ''
            }
        };
    },
    computed: {
        modalTitle() {
            const prefix = this.isUpdateFlag ? 'Update' : 'Create';
            let locationType = '';
            if (this.warehouseId) {
                locationType = 'Warehouse';
            } else if (this.assetId) {
                locationType = 'Asset';
            } else if (this.teamMemberPublicId) {
                locationType = 'Team Member';
            }

            return `${prefix} ${locationType} Location`;
        }
    },
    methods: {
        async save() {
            const isValid = this.validateLocationDetails();
            if (!isValid) 
                return;
            this.$_handleLoaderState(true, 'PROCESSING...');
            if (this.isUpdateFlag) {
                await this.handleUpdate();
                return;
            }
            await this.handleCreate();
        },
        async handleUpdate() {
            const api = `/api/item-locations/${this.locationId}`;
            const payload = {
                method: 'put',
                data: {
                    ...this.locationDetails,
                    name: this.locationDetails.locationName,
                    type: this.locationDetails.locationType
                }
            };

            if (this.warehouseId) {
                payload.data.warehouseId = this.warehouseId;
            } else if (this.assetId) {
                payload.data.assetId = this.assetId;
            } else if (this.teamMemberPublicId) {
                payload.data.teamMemberPublicId = this.teamMemberId;
            }
            try {
                const response = await handleRequests(api, payload);
                if (response.status !== 200) {
                    showErrorMessage(this, 'Error in updating the location', response);
                    this.$_handleLoaderState(false);
                    return;
                }
                this.$notify({
                    message: 'Location updated successfully',
                    type: 'success'
                });
                this.$v.$reset();
                this.resolve('ok');
                return;
            } catch (error) {
                showErrorMessage(this, 'Error in updating location', error);
            }
            this.$_handleLoaderState(false);
        },
        async handleCreate() {
            const api = `/api/item-locations`;
            const payload = {
                method: 'post',
                data: {
                    ...this.locationDetails,
                    type: this.locationDetails.locationType,
                    name: this.locationDetails.locationName
                }
            };

            if (this.warehouseId) {
                payload.data.warehouseId = this.warehouseId;
            } else if (this.assetId) {
                payload.data.assetId = this.assetId;
            } else if (this.teamMemberPublicId) {
                payload.data.teamMemberPublicId = this.teamMemberId;
            }

            try {
                const response = await handleRequests(api, payload);
                if (response.status !== 201) {
                    showErrorMessage(this, 'Error in creating the location', response);
                    return;
                }
                this.$notify({
                    message: 'Location created successfully',
                    type: 'success'
                });
                this.$v.$reset();
                this.resolve('ok');
                return;
            } catch (error) {
                showErrorMessage(this, 'Error in creating the location', error);
            }
            this.$_handleLoaderState(false);
        },
        validateLocationDetails() {
            this.$v.locationDetails.$touch();
            if (this.$v.locationDetails.$invalid) {
                const message = 'Please fix the errors and try again.';
                this.$notify({
                    message,
                    type: 'danger'
                });
                return false;
            }
            return true;
        },
        generateRandomHexColor() {
            const letters = '0123456789ABCDEF';

            // HTML color code starts with #
            let color = '#';

            // Generating 6 times as HTML color code
            // consist of 6 letter or digits
            for (let i = 0; i < 6; i++) 
                color += letters[Math.floor(Math.random() * 16)];

            return color;
        }
    },
    validations: {
        locationDetails: {
            locationName: {
                required,
                maxLength: maxLength(30)
            },
            locationType: {
                required
            }
        }
    }
};
</script>

<style scoped lang="scss">
.color-form-group {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.color-picker {
    border: none;
    padding: 0;
    margin: 0;
}

::v-deep .md-menu-content.md-select-menu {
    width: 150px;
}
.modal-container {
    max-width: 600px;
}

::v-deep .md-switch {
    .md-switch-label {
        color: #3c4858 !important;
    }
}
</style>
